import { TSelectedAvailability } from 'webReservations/context/AvailabilityContext';
import { useRestaurantContext } from 'webReservations/context/RestaurantContext';
import { useReservationDetails } from 'webReservations/hooks/useReservationDetails';
import { TERMS_OF_SERVICE_PATH } from 'webReservations/paths';
import { ReservationWithDetails } from 'webReservations/reservationDetails/apiHelpers';
import { reservationTableDisplayName } from 'webReservations/utils/reservationTableDisplayName';
import { selectedAvailabilityDisplayName } from 'webReservations/utils/selectedAvailabilityDisplayName';
import { Button, ButtonVariants } from '@components/button/Button';
import { Checkbox } from '@components/checkbox/Checkbox';
import { CheckoutReceiptDetails } from '@components/checkout/CheckoutReceiptDetails';
import { Divider } from '@components/divider/Divider';
import { Error as ErrorMessage } from '@components/error/Error';
import { PaymentElement } from '@components/stripe/PaymentElement';
import { NewStripeElements } from '@components/stripe/StripeElements';
import { PricingInfo } from '@shared/pricingInfoApi';
import { isCancellationPolicyApplicable } from '@shared/reservations/reservationUtil';
import { centsToWholeDollar } from '@shared/utils/currency';
import { parseToDayMonthFormat } from '@shared/utils/dateFormatters';
import { ISOTimeTo12HourTime } from '@shared/utils/time';
import cx from 'classnames';
import { type ChangeEvent, PropsWithChildren, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import linkStyles from '~styles/links.scss';
import typography from '~styles/typography.scss';
import { TransactionLineItem } from './apiHelpers';
import { CancellationPolicyNotice } from './CancellationPolicyNotice';
import styles from './Checkout.scss';
import { CheckoutReservationDetails } from './CheckoutReservationDetails';
import { useStripePaymentElements } from './useStripePaymentElements';

const CheckoutHeader = ({ children }: PropsWithChildren) => (
  <h1 className={cx(typography.d3, styles.header)}>{children}</h1>
);

/**
 * Summary of reservation (date, time, guest count)
 */
const CheckoutSummary = ({
  isBestAvailable,
  publicName,
  guestCount,
  date,
  time,
}: {
  isBestAvailable: boolean;
  publicName: string;
  guestCount: number;
  date: string; // ISO date string
  time: string; // ISO time string
}) => {
  const displayName = selectedAvailabilityDisplayName({
    isBestAvailable,
    publicName,
  });

  return (
    <CheckoutReservationDetails
      guestCount={guestCount}
      date={date}
      time={time}
      displayName={displayName}
    />
  );
};

const CheckoutReceipt = ({
  fee,
  tax,
  total,
  lineItems,
}: {
  fee: number;
  tax: number;
  total: number;
  lineItems: TransactionLineItem[];
}) => (
  <div className={styles.receipt}>
    <CheckoutReceiptDetails
      fee={fee}
      hideZeroFee
      lineItems={lineItems}
      tax={tax}
      total={total}
    />
  </div>
);

const CheckoutPolicies = ({
  date,
  purchasePrice,
  availabilityTime,
}: {
  date: string;
  purchasePrice?: number;
  availabilityTime?: string;
}) => (
  <>
    <Link
      className={cx(linkStyles.linkPrimary, styles.refundPolicyLink)}
      to={`${TERMS_OF_SERVICE_PATH}#cancellations-and-refunds`}
      target="_blank"
    >
      Refund Policy
    </Link>

    <CancellationPolicyNotice
      date={date}
      purchasePrice={purchasePrice}
      availabilityTime={availabilityTime}
    />
  </>
);

export const MARKETING_OPT_IN_LABEL =
  'Sign me up to receive offers and news from this restaurant by email or text.';

const CheckoutForm = ({
  errorMessage,
  handleCheckoutSubmit,
  isSubmitDisabled = false,
  marketingOptIn,
  onMarketingOptInChange,
  pricingInfo,
}: {
  errorMessage?: string;
  handleCheckoutSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  isSubmitDisabled?: boolean;
  marketingOptIn?: boolean;
  onMarketingOptInChange?: (value: boolean) => void;
  pricingInfo: PricingInfo & {
    subtotal: number;
    lineItems: TransactionLineItem[];
  };
}) => {
  const { restaurantDetails } = useRestaurantContext();
  const { handlePaymentElementOnChange, isFormComplete } =
    useStripePaymentElements();
  const navigate = useNavigate();

  const { cancellationPolicy } = restaurantDetails;

  const hasApplicableCancellationPolicy =
    !!cancellationPolicy &&
    isCancellationPolicyApplicable(
      pricingInfo.subtotal,
      cancellationPolicy.threshold,
    );

  const requireImmediatePayment = pricingInfo.total > 0;

  const requirePaymentMethod =
    requireImmediatePayment || hasApplicableCancellationPolicy;

  return (
    <form className={styles.checkoutForm} onSubmit={handleCheckoutSubmit}>
      {requirePaymentMethod && (
        <PaymentElement onChange={handlePaymentElementOnChange} />
      )}
      {onMarketingOptInChange && (
        <Checkbox
          className={styles.marketingOptIn}
          checked={!!marketingOptIn}
          label={MARKETING_OPT_IN_LABEL}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onMarketingOptInChange(event.target.checked)
          }
        />
      )}
      <ErrorMessage message={errorMessage} />
      <Button
        className={styles.bookAndPay}
        isDisabled={
          (requirePaymentMethod && !isFormComplete) || isSubmitDisabled
        }
        label={requireImmediatePayment ? 'Book and Pay' : 'Book'}
        type="submit"
        variant={ButtonVariants.Primary}
      />
      <Button
        className={styles.cancel}
        label="Cancel"
        onClick={() => navigate(-1)}
        variant={ButtonVariants.Tertiary}
      />
    </form>
  );
};

/**
 * Styled card container for checkout sections
 */
const CheckoutCard = ({ children }: PropsWithChildren) => (
  <section className={styles.checkoutSection}>{children}</section>
);

const CheckoutSeparator = () => <Divider className={styles.divider} />;

interface CheckoutPageComponentProps {
  children: ReactNode;
  total: number;
}

/**
 * Container for the checkout UI
 */
const CheckoutPageComponent = ({
  children,
  total,
}: CheckoutPageComponentProps) => (
  <main className={styles.container}>
    <div className={styles.content}>
      <NewStripeElements amount={total}>{children}</NewStripeElements>
    </div>
  </main>
);
CheckoutPageComponent.displayName = 'CheckoutPage';

interface CheckoutDeltaContainerProps {
  availability: Pick<
    TSelectedAvailability,
    | 'date'
    | 'time'
    | 'isBestAvailable'
    | 'publicName'
    | 'purchasePrice'
    | 'listingId'
  > & {
    guestCount: number;
  };
}

export const CheckoutDeltaContainer = ({
  availability,
}: CheckoutDeltaContainerProps) => {
  const { data: reservation, isLoading: isReservationLoading } =
    useReservationDetails();

  if (
    isReservationLoading ||
    !reservation ||
    availability.purchasePrice === null
  )
    return null;

  return (
    <CheckoutDelta reservation={reservation} availability={availability} />
  );
};

interface CheckoutDeltaProps {
  reservation: ReservationWithDetails;
  availability: Pick<
    TSelectedAvailability,
    | 'date'
    | 'time'
    | 'isBestAvailable'
    | 'publicName'
    | 'purchasePrice'
    | 'listingId'
  > & {
    guestCount: number;
  };
}

export const CheckoutDelta = ({
  availability,
  reservation,
}: CheckoutDeltaProps) => {
  const deltaItems = [
    {
      header: 'Reservation Date',
      before: parseToDayMonthFormat(reservation.date),
      after: parseToDayMonthFormat(availability.date),
    },
    {
      header: 'Reservation Time',
      before: ISOTimeTo12HourTime(reservation.time),
      after: ISOTimeTo12HourTime(availability.time),
    },
    {
      header: 'Table',
      before: reservationTableDisplayName(reservation),
      after: selectedAvailabilityDisplayName({
        isBestAvailable: availability.isBestAvailable,
        publicName: availability.publicName,
      }),
    },
    {
      header: 'Guest Count',
      before: reservation.guestCount,
      after: availability.guestCount,
    },
    {
      header: 'Table Price',
      before: centsToWholeDollar(reservation.transaction.price),
      after: centsToWholeDollar(availability.purchasePrice as number),
    },
  ].filter((item) => item.before || item.after);

  return (
    <dl className={styles.deltaContainer}>
      {deltaItems.map((deltaItem) => {
        return (
          <div className={styles.deltaItem} key={deltaItem.header}>
            <dt className={cx(typography.t1, styles.deltaItemHeader)}>
              {deltaItem.header}
            </dt>

            <dd className={styles.deltaBeforeAndAfter}>
              <span className={cx(styles.deltaBefore, styles.strikethrough)}>
                {deltaItem.before}
              </span>
              <span className={styles.deltaArrow} aria-hidden="true">
                →
              </span>
              <span className={styles.deltaAfter}>{deltaItem.after}</span>
            </dd>
          </div>
        );
      })}
    </dl>
  );
};

export default Object.assign(CheckoutPageComponent, {
  Card: CheckoutCard,
  Separator: CheckoutSeparator,
  Header: CheckoutHeader,
  Summary: CheckoutSummary,
  Delta: CheckoutDeltaContainer,
  Receipt: CheckoutReceipt,
  Form: CheckoutForm,
  Policies: CheckoutPolicies,
});
